import React, { useState, useCallback, useMemo, useEffect } from 'react';
import { Box, Button, Stack, Typography, IconButton, SvgIcon, Tooltip } from '@mui/material';
import ClearIcon from 'src/icons/untitled-ui/free/x-clear';
import RadioButtonCheckedIcon from '@mui/icons-material/RadioButtonChecked'; // For selected state
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked'; // For unselected state
import { FileDropzone } from './only-dropzone'; // Assuming this is a custom component
import ZoomImage from 'src/components/zoom-image';

const StyledBox = ({ children, ...props }) => (
  <Box
    sx={{
      border: 1,
      borderColor: 'divider',
      borderRadius: 1,
      height: 64,
      mt: 1,
      position: 'relative',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      ...props.sx, // Allow additional styles to be passed in
    }}
    {...props}
  >
    {children}
  </Box>
);

const MultiImageUploader = ({ 
  formik, 
  maxFiles = 3, 
  maxSelect = 0, // New prop for maximum selection limit
  useSelect = false, 
  onImageSelected,
  onImageRefresh,
  label = 'Chọn một hoặc nhiều hình để tải lên'
}) => {
  const [images, setImages] = useState([]);
  const [errorMsg, setErrorMsg] = useState(null);

  useEffect(() => {
    const arrays = Array.isArray(formik.values.defaultImgs)
      ? formik.values.defaultImgs
      : [];
    setImages(arrays);
  }, [formik.values.defaultImgs]);

  useEffect(() => {
    if (!formik.isSubmitting) {
      if (formik.errors.images) {
        setErrorMsg(formik.errors.images);
      } else {
        setErrorMsg(null);
      }
    }
  }, [formik.isSubmitting, formik.values.images]);

  useEffect(() => {
    if (onImageRefresh && onImageRefresh.name) {
      setImages((prevImages) => {
        const updatedImages = prevImages.map((image) =>
          image.name === onImageRefresh.name
            ? { ...image, location: onImageRefresh.location, timestamp: onImageRefresh.timestamp } // Add or update location
            : image
        );
  
        // Use Formik to update the field value after the render cycle
        formik.setFieldValue('images', updatedImages);
        return updatedImages;
      });
    }
  }, [onImageRefresh]);

  // Memoize the image URLs to avoid re-calculating them on every render
  const imagePreviews = useMemo(() => {
    return images.map((image) => ({
      preview: image.file ? URL.createObjectURL(image.file) : image.name,
      selected: image.selected,
      disabled: image.disabled,
      readonly: image.readonly,
    }));
  }, [images]);

  // Handle file drop for multiple images and set their previews
  const handleImageDrop = useCallback(
    (acceptedFiles) => {
      setImages((prevImages) => {
        const newImages = acceptedFiles.map((file) => ({
          file,
          name: file.name,
          modified: file.lastModified || 0,
          selected: false, // Default: not selected
        }));
        const updatedImages = [...prevImages, ...newImages];
        const limitedImages = updatedImages.slice(0, maxFiles);
        formik.setFieldValue('images', limitedImages);
        return limitedImages;
      });
    },
    [formik, maxFiles]
  );

  const handleSelectionChange = (index) => {
    setImages((prevImages) => {
      const selectedCount = prevImages.filter((img) => img.selected).length;

      // Skip action if maxSelect limit is reached and the current item is not selected
      if (maxSelect > 0 && selectedCount >= maxSelect && !prevImages[index].selected) {
        return prevImages;
      }
      const updatedImages = prevImages.map((image, i) => {
        if (i === index) {
          return { ...image, selected: !image.selected };
        }
        return image;
      });

      // const updatedImages = prevImages.map((image, i) => {
      //   if (i === index) {
      //     let isSelected = false;
      //     if(selectedCount < maxSelect){
      //       isSelected = !image.selected;
      //     }
      //     // Prevent selection if maxSelect limit is reached
      //     if (isSelected && maxSelect > 0 && selectedCount >= maxSelect) {
      //       return image
      //     }
      //     return { ...image, selected: isSelected };
      //   }

      //   return image;
      // });
      
      formik.setFieldValue('images', updatedImages);
  
      // Pass selected image file objects to parent for detection
      if (onImageSelected) {
        // Create a list of selected images
        const selectedImages = updatedImages
          .filter((img) => img.selected)
          .map((img) => ({
            file: img.file ? URL.createObjectURL(img.file) : null, // If file is available, use the URL
            name: img.name, // Keep the image name for reference
            modified: img.modified,
          }));
        
        // Find the newly selected image
        const newSelectedImage = updatedImages[index].selected
          ? {
              file: updatedImages[index].file
                ? URL.createObjectURL(updatedImages[index].file)
                : null,
              name: updatedImages[index].name,
              modified: updatedImages[index].modified,
            }
          : null;
  
        // Add the new selected image to the beginning of the list
        const reorderedSelectedImages = newSelectedImage
          ? [newSelectedImage, ...selectedImages.filter((img) => img.name !== newSelectedImage.name)]
          : selectedImages;

        // Pass the reordered list to the parent
        onImageSelected(reorderedSelectedImages || []);
      }
  
      return updatedImages;
    });
  };
  

  const handleImageRemove = (index) => {
    setImages((prevImages) => {
      const updatedImages = prevImages.filter((_, i) => i !== index);
      formik.setFieldValue('images', updatedImages); // Update Formik's images field
      return updatedImages;
    });
  };

  return (
    <Stack spacing={3}>
      <Box display="flex" justifyContent="center" height="auto">
        <FileDropzone
          accept={{ 'image/*': [] }}
          maxFiles={maxFiles}
          onDrop={handleImageDrop}
          caption="(SVG, JPG, PNG, or GIF)"
          files={images}
          disabled={images.length >= maxFiles}
        />
      </Box>

      {imagePreviews.length > 0 ? (
        <Stack spacing={2}>
          {imagePreviews.map(({ preview, selected, disabled, readonly }, index) => (
            <StyledBox key={index}>
              <ZoomImage thumbSrc={preview} zoomSrc={preview} alt="Zoomed Preview" height="54" />
              {useSelect && !disabled && (
                <Tooltip title={readonly ? "Readonly" : selected ? "Unselect" : "Select"}>
                  <span>
                  <IconButton
                    sx={{
                      position: 'absolute',
                      top: '50%',
                      left: 12,
                      transform: 'translateY(-50%)',
                      backgroundColor: readonly ? 'gray' : 'white',
                    }}
                    onClick={() => handleSelectionChange(index)}
                    disabled={readonly}
                  >
                    <SvgIcon>
                      {selected ? (
                        <RadioButtonCheckedIcon color="primary" />
                      ) : (
                        <RadioButtonUncheckedIcon color="disabled" />
                      )}
                    </SvgIcon>
                  </IconButton>
                  </span>
                </Tooltip>
              )}
              <Tooltip title="Xóa">
                <IconButton
                  edge="end"
                  sx={{
                    position: 'absolute',
                    top: '50%',
                    right: 12,
                    transform: 'translateY(-50%)',
                    bgcolor: 'white',
                  }}
                  onClick={(e) => {
                    e.stopPropagation();
                    handleImageRemove(index);
                  }}
                >
                  <SvgIcon>
                    <ClearIcon />
                  </SvgIcon>
                </IconButton>
              </Tooltip>
            </StyledBox>
          ))}
        </Stack>
      ) : (
        <StyledBox>
          {errorMsg ? (
            <Typography align="center" color="error" variant="contained">
              {errorMsg}
            </Typography>
          ) : (
            <Typography align="center" color="text.secondary" variant="contained">
              {label}
            </Typography>
          )}
        </StyledBox>
      )}
  
      {images.length > 0 && (
        <Box display="flex" justifyContent="flex-end">
          <Button
            color="inherit"
            onClick={() => {
              setImages([]);
              formik.setFieldValue('images', []);
            }}
            size="small"
            type="button"
          >
            Xóa toàn bộ
          </Button>
        </Box>
      )}
    </Stack>
  );
};

export default MultiImageUploader;

/**
 * How to use
 * import MultiImageUploader from 'src/components/uploads/images-uploader';
 * <MultiImageUploader formik={formik} maxFiles={6} onImageSelect={(image) => {}}  />
 */
